// 〔＋ 指派〕三步（SPEC #org-tree-ui 第 2 點、o07 / o07b；受託方 o15）：
//   選人 / 成員群組 → 選角色（每個選項直接寫動作）→ 期限 / 時段（預設永久）→ 確認（涵蓋目前 N 個戶、M 台；之後新增的也涵蓋）→ step-up
//   - 對象是自己、或含自己的成員群組 = 自我授權：必填理由、建議設期限、黃框寫誰會收到通知，主按鈕「指派自己」
//   - 受託方（mode = delegation）：節點欄是委派子樹挑選器（只有名稱與結構數）；角色區「實際生效 = 角色 ∩ 委派範圍」，超出的灰字「不會生效」；
//     撤防 / 布防 / 維運 / 匯出不從角色帶入（選單 19），要另勾；第一次被指派到委派節點的人會看到「<委派方> 看得到你的顯示名稱…」
//   - 編輯（existing）：同一個對話框，存檔時受託方這一筆的實際生效照目前範圍重算
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isKinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import type { MyOrg } from '../../lib/planeApi';
import { EXTRA_ACTIONS, NODE_ROLES, treeApi, type Assignment, type AssignmentIn, type Delegation, type NodeRole, type Subtree } from '../../lib/orgTreeApi';
import { actionWords, intersectScope, roleActions } from '../../lib/orgTreeUi';
import { memberName } from '../people/MembersTab';
import { selfNotifyKey, useMemberGroups, useOrgMembers, useRoleTemplates } from './hooks';
import { DelegatedNodePicker } from './DelegatedNodePicker';
import { otErr } from './otErr';
import { dateStamp } from '../../lib/timefmt';

export type AssignTarget =
  | { kind: 'node'; nodeId: string; nodeName: string; subtree?: Subtree }
  | { kind: 'delegation'; delegation: Delegation; nodeId?: string; nodeName?: string };

export function AssignDialog({
  org,
  target,
  existing,
  close,
  done,
}: {
  org: MyOrg;
  target: AssignTarget | null;
  existing?: Assignment | null;
  close(): void;
  done(a: Assignment): void;
}) {
  const { t } = useTranslation();
  const open = !!target;
  const members = useOrgMembers(org, open);
  const groups = useMemberGroups(org, open);
  const templates = useRoleTemplates(org, open);
  const p6 = target?.kind === 'delegation';
  const scopes = p6 ? target.delegation.scopes : null;

  const [step, setStep] = useState(0);
  const [nodeId, setNodeId] = useState('');
  const [nodeName, setNodeName] = useState('');
  const [nodeSubtree, setNodeSubtree] = useState<Subtree | undefined>();
  const [who, setWho] = useState(''); // user:<id> / group:<id>
  const [filter, setFilter] = useState('');
  const [role, setRole] = useState<NodeRole>('operator');
  const [tpl, setTpl] = useState('');
  const [extra, setExtra] = useState<string[]>([]);
  const [until, setUntil] = useState('');
  const [win, setWinState] = useState<{ from: string; to: string } | null>(null);
  // 使用者沒動時段：送出原本的 schedule（多個 window、星期、原時區都保留，不因為改角色就放寬時段）
  const [winTouched, setWinTouched] = useState(false);
  const setWin = (w: { from: string; to: string } | null) => {
    setWinTouched(true);
    setWinState(w);
  };
  const [reason, setReason] = useState('');
  const [needReason, setNeedReason] = useState(false);
  // 本機錄影全部歷史（SPEC #device-permissions-local-playback）：只有 owner / org_admin 能改；受託方要委派方先開
  const [lpf, setLpf] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Assignment | null>(null);

  useEffect(() => {
    if (!target) return;
    setStep(existing ? 1 : 0);
    setErr(null);
    setResult(null);
    setReason('');
    setNeedReason(false);
    setFilter('');
    setNodeId(target.kind === 'node' ? target.nodeId : (target.nodeId ?? existing?.node_id ?? ''));
    setNodeName(target.kind === 'node' ? target.nodeName : (target.nodeName ?? ''));
    setNodeSubtree(target.kind === 'node' ? target.subtree : undefined);
    setWho(existing ? `${existing.principal_kind}:${existing.principal_id}` : '');
    setRole(existing?.role ?? 'operator');
    setTpl(existing?.role_template_ref ?? '');
    setExtra(existing?.extra_actions ?? []);
    setUntil(existing?.expires_at ? existing.expires_at.slice(0, 10) : '');
    const w = existing?.schedule?.windows?.[0];
    setWinState(w ? { from: w.from, to: w.to } : null);
    setWinTouched(false);
    setLpf(!!existing?.local_playback_full);
  }, [target, existing]);

  const me = (members.data ?? []).find((m) => m.is_me);
  const [kind, id] = who.split(':') as ['user' | 'group', string];
  const group = kind === 'group' ? (groups.data ?? []).find((g) => g.group_id === id) : undefined;
  const isSelf = !!me && ((kind === 'user' && id === me.user_id) || (kind === 'group' && !!group?.members?.some((m) => m.user_id === me.user_id)));
  const tplActions = (templates.data ?? []).find((r) => r.role_id === tpl)?.actions ?? [];
  const actions = roleActions(role, extra, tplActions, p6);
  const eff = scopes ? intersectScope(actions, scopes) : { effective: actions, ignored: [] };
  const whoName = kind === 'group' ? (group?.name ?? '') : memberName((members.data ?? []).find((m) => m.user_id === id), id);
  const notify = selfNotifyKey(org, members.data);
  const fromOrg = p6 ? target.delegation.from_org_name : '';
  const orgAdmin = org.role === 'owner' || org.role === 'org_admin';
  const lpfAllowed = orgAdmin && (!p6 || !!target?.delegation.local_playback_full);
  const lpfChanged = lpf !== !!existing?.local_playback_full;

  const people = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const ms = (members.data ?? []).filter((m) => m.status !== 'suspended' && (!f || memberName(m).toLowerCase().includes(f) || (m.email ?? '').toLowerCase().includes(f)));
    const gs = (groups.data ?? []).filter((g) => !f || g.name.toLowerCase().includes(f));
    return { ms, gs };
  }, [members.data, groups.data, filter]);

  async function submit() {
    if (!target) return;
    if ((isSelf || needReason) && !reason.trim()) {
      setNeedReason(true);
      setErr(t('org.err_REASON_REQUIRED'));
      return;
    }
    setBusy(true);
    setErr(null);
    const body: AssignmentIn = {
      principal_kind: kind,
      principal_id: id,
      role,
      ...(role === 'custom' && tpl ? { role_template_ref: tpl } : {}),
      extra_actions: extra,
      expires_at: until ? new Date(until + 'T23:59:59').toISOString() : null,
      schedule: !winTouched
        ? (existing?.schedule ?? null)
        : win
          ? {
              tz: existing?.schedule?.tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
              windows: [{ ...(existing?.schedule?.windows?.[0] ?? {}), from: win.from, to: win.to }, ...(existing?.schedule?.windows?.slice(1) ?? [])],
            }
          : null,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      // 只在有權限改、而且改了（或新建要開）時才帶：node_admin 帶了會 403
      ...(lpfAllowed && (existing ? lpfChanged : lpf) ? { local_playback_full: lpf } : {}),
    };
    try {
      let r: Assignment;
      if (target.kind === 'node') {
        r = existing ? await treeApi.updateAssignment(org, existing.node_id, existing, body) : await treeApi.createAssignment(org, target.nodeId, body);
      } else {
        r = existing ? await treeApi.dUpdate(org, target.delegation.delegation_id, existing, body) : await treeApi.dAssign(org, target.delegation.delegation_id, nodeId, body);
      }
      if (p6 && (r?.ignored_actions?.length ?? 0) > 0) setResult(r);
      else {
        done(r);
        close();
      }
    } catch (e) {
      if (isKinioError(e) && e.code === 'REASON_REQUIRED') {
        setNeedReason(true);
        setErr(t('ot.reason_needed_self'));
      } else setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }

  const title = existing
    ? t('ot.assign_edit_title', { name: existing.display_name })
    : p6
      ? t('ot.assign_d_title', { path: [fromOrg, nodeName].filter(Boolean).join(' › ') })
      : t('ot.assign_title', { node: nodeName });
  const steps = [t('ot.step_who'), t('ot.step_role'), t('ot.step_term'), t('ot.step_confirm')];
  const roleWords = (r: NodeRole) => {
    const as = roleActions(r, [], r === 'custom' ? tplActions : [], p6);
    if (r === 'custom' && !tpl) return t('ot.pick_template');
    if (!scopes) return t('ot.role_desc_' + r, { defaultValue: actionWords(t, as) });
    const x = intersectScope(as, scopes);
    return actionWords(t, x.effective) + (r === 'node_admin' ? t('ot.role_desc_node_admin_d_tail', { from: fromOrg }) : '');
  };

  return (
    <Dialog open={open} title={title} onClose={close} wide>
      {result ? (
        <div data-testid="assign-result">
          <p>{t('ot.assign_done')}</p>
          <p className="ot-err-box small">{t('ot.not_effective', { what: actionWords(t, result.ignored_actions ?? []) })}</p>
          <p className="small">{t('ot.effective_now', { what: actionWords(t, result.effective_actions ?? []) })}</p>
          <div className="dialog-actions">
            <button
              className="btn btn-primary"
              onClick={() => {
                done(result);
                close();
              }}
            >
              {t('common.done')}
            </button>
          </div>
        </div>
      ) : (
        <div className="form" data-testid="assign-dialog" data-step={step}>
          <ol className="ot-steps" aria-label={t('ot.steps')}>
            {steps.map((s, i) => (
              <li key={s} aria-current={i === step ? 'step' : undefined} className={i === step ? 'on' : i < step ? 'done' : ''}>
                {s}
              </li>
            ))}
          </ol>
          {step === 0 && (
            <>
              {p6 && (
                <div className="field">
                  <span>{t('ot.d_node_label')}</span>
                  <DelegatedNodePicker
                    org={org}
                    delegation={target.delegation}
                    value={nodeId}
                    onPick={(n) => {
                      setNodeId(n.node_id);
                      setNodeName(n.name);
                      setNodeSubtree(n.subtree);
                    }}
                  />
                </div>
              )}
              <label className="field">
                <span>{t('ot.who')}</span>
                <input className="input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('ot.who_search')} aria-label={t('ot.who_search')} />
              </label>
              <div className="ot-pick-list" role="listbox" aria-label={t('ot.who')}>
                {people.gs.map((g) => (
                  <label key={g.group_id} className="ot-pick">
                    <input type="radio" name="ot-who" checked={who === 'group:' + g.group_id} onChange={() => setWho('group:' + g.group_id)} />▦ {g.name}{' '}
                    <span className="muted small">{t('ot.member_group_n', { n: g.member_count })}</span>
                  </label>
                ))}
                {people.ms.map((m) => (
                  <label key={m.user_id} className="ot-pick">
                    <input type="radio" name="ot-who" checked={who === 'user:' + m.user_id} onChange={() => setWho('user:' + m.user_id)} />
                    {memberName(m)}
                    {m.is_me && <span className="badge">{t('ot.you')}</span>}
                    {m.email && m.display_name && <span className="muted small"> {m.email}</span>}
                  </label>
                ))}
              </div>
            </>
          )}
          {step === 1 && (
            <fieldset className="field ot-roles">
              <legend>{scopes ? t('ot.role_effective_title', { scope: actionWords(t, scopes) }) : t('ot.role')}</legend>
              {NODE_ROLES.filter((r) => r !== 'custom' || (templates.data?.length ?? 0) > 0).map((r) => {
                const ign = scopes ? intersectScope(roleActions(r, [], r === 'custom' ? tplActions : [], true), scopes).ignored : [];
                return (
                  <label key={r} className="ot-pick">
                    <input type="radio" name="ot-role" checked={role === r} onChange={() => setRole(r)} />
                    <strong>{t('ot.role_' + r)}</strong> → {roleWords(r)}
                    {ign.length > 0 && <span className="muted small"> {t('ot.not_effective_paren', { what: actionWords(t, ign) })}</span>}
                    {r === 'node_admin' && !p6 && <span className="muted small"> {t('ot.role_node_admin_excl')}</span>}
                  </label>
                );
              })}
              {role === 'custom' && (
                <select className="input" value={tpl} onChange={(e) => setTpl(e.target.value)} aria-label={t('ot.role_custom')}>
                  <option value="">—</option>
                  {(templates.data ?? []).map((x) => (
                    <option key={x.role_id} value={x.role_id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              )}
              <div className="ot-extra">
                {EXTRA_ACTIONS.filter((a) => (p6 ? true : a === 'recording.export' || !roleActions(role, [], tplActions).includes(a)))
                  .filter((a) => !scopes || scopes.includes(a) || extra.includes(a))
                  .map((a) => (
                    <label key={a} className="row-inline">
                      <input
                        type="checkbox"
                        checked={extra.includes(a)}
                        onChange={(e) => setExtra(e.target.checked ? [...extra, a] : extra.filter((x) => x !== a))}
                      />
                      {t('ot.extra_' + a.replace('.', '_'))}
                    </label>
                  ))}
              </div>
              {scopes && eff.ignored.length > 0 && (
                <p className="ot-err-box small" data-testid="ignored-actions">
                  {t('ot.ignored_box', { role: t('ot.role_' + role), what: actionWords(t, eff.ignored), from: fromOrg })}
                  <br />
                  <span className="muted">{t('ot.effective_now', { what: actionWords(t, eff.effective) })}</span>
                </p>
              )}
            </fieldset>
          )}
          {step === 2 && (
            <>
              <label className="field">
                <span>{t('ot.term')}</span>
                <select className="input" value={until ? 'date' : 'forever'} onChange={(e) => setUntil(e.target.value === 'date' ? new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10) : '')}>
                  <option value="forever">{t('ot.forever')}</option>
                  <option value="date">{t('ot.until_date')}</option>
                </select>
              </label>
              {until && <input className="input" type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label={t('ot.until_date')} />}
              <label className="row-inline">
                <input type="checkbox" checked={!!win} onChange={(e) => setWin(e.target.checked ? { from: '19:00', to: '07:00' } : null)} />
                {t('ot.only_window')}
              </label>
              {win && (
                <div className="row">
                  <input className="input" type="time" style={{ width: 'auto' }} value={win.from} onChange={(e) => setWin({ ...win, from: e.target.value })} aria-label={t('ot.window_from')} />–
                  <input className="input" type="time" style={{ width: 'auto' }} value={win.to} onChange={(e) => setWin({ ...win, to: e.target.value })} aria-label={t('ot.window_to')} />
                </div>
              )}
              {isSelf && !until && <p className="muted small">{t('ot.self_suggest_term')}</p>}
              {(lpfAllowed || lpf) && (
                <>
                  <label className="row-inline">
                    <input type="checkbox" checked={lpf} disabled={!lpfAllowed} onChange={(e) => setLpf(e.target.checked)} data-testid="assign-lpf" />
                    {t('ot.local_playback_full')}
                  </label>
                  <p className="muted small">{t('ot.local_playback_full_hint')}</p>
                </>
              )}
            </>
          )}
          {step === 3 && (
            <div data-testid="assign-confirm">
              <p>
                <strong>{whoName}</strong> · {role === 'custom' ? ((templates.data ?? []).find((x) => x.role_id === tpl)?.name ?? t('ot.role_custom')) : t('ot.role_' + role)} ·{' '}
                {until ? t('ot.until', { date: dateStamp(until) }) : t('ot.forever')}
                {win && ' · ' + t('ot.window', win)}
              </p>
              <p className="small">
                {nodeSubtree ? t('ot.covers_now', { homes: nodeSubtree.homes, devices: nodeSubtree.devices }) : t('ot.covers_subtree')} {t('ot.covers_tail')}
              </p>
              <p className="small">{t('ot.effective_now', { what: actionWords(t, eff.effective) })}</p>
              {p6 && <p className="muted small">{t('ot.d_visible_to_delegator', { from: fromOrg })}</p>}
              {(isSelf || needReason) && (
                <>
                  <p className="ot-warn small" data-testid="self-assign">
                    {t('ot.self_yellow', { scope: nodeName })} {p6 ? t('ot.self_notify_d', { from: fromOrg }) : t('ot.self_notify_' + notify)}
                  </p>
                  <label className="field">
                    <span>{t('ot.reason')} *</span>
                    <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
                  </label>
                </>
              )}
            </div>
          )}
          {err && (
            <p className="error" role="alert">
              {err}
            </p>
          )}
          <div className="dialog-actions">
            <button className="btn" onClick={step === 0 || (existing && step === 1) ? close : () => setStep(step - 1)}>
              {step === 0 || (existing && step === 1) ? t('common.cancel') : t('common.back')}
            </button>
            {step < 3 ? (
              <button
                className="btn btn-primary"
                disabled={(step === 0 && (!who || (p6 && !nodeId))) || (step === 1 && role === 'custom' && !tpl) || (step === 1 && eff.effective.length === 0)}
                onClick={() => setStep(step + 1)}
              >
                {t('ot.next')}
              </button>
            ) : (
              <button className="btn btn-primary" disabled={busy || ((isSelf || needReason) && !reason.trim())} onClick={() => void submit()} data-testid="assign-submit">
                {isSelf ? t('ot.assign_self') : existing ? t('common.save') : t('ot.assign')}
                {t('ot.reauth_suffix')}
              </button>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
